import { useEventListener, useOnline } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onScopeDispose, ref, toValue, watch } from 'vue';

import useStreamsStore from '@/stores/streams';

// An MJPEG preview is a single long-lived multipart response painted by a plain <img>.
// When that connection dies -- station restart, VPN blip, laptop lid closed, proxy
// dropping the upstream -- the element is left dead: `src` never changed, so the
// browser has no reason to ask again. Everything here exists to notice that and force
// a new connection.
//
// The catch is that a cut multipart stream is mostly silent. Chrome fires no `error`
// event; it just discards the decoded frame, which surfaces as naturalWidth falling
// back to 0. Polling that is the only signal available for a cross-origin stream, so it
// drives recovery, and the error event (when a browser does bother to fire one) is
// treated as an extra nudge rather than the mechanism.

const LIVENESS_POLL = 1000;

// Consecutive zero-width reads before declaring a stream that was working dead. Two
// keeps one unlucky sample from triggering a pointless reconnect.
const DEAD_READS = 2;

// A connection that has never painted anything gets far longer, since it may simply be
// slow to produce its first frame rather than wedged.
const FIRST_FRAME_TIMEOUT = 20000;

// Backoff between retries, capped low and retried forever. This is a live monitoring
// wall: a station that has been down for an hour still needs to come back within a few
// seconds of returning, so the cap is worth more than sparing it the poll traffic.
const RETRY_DELAYS = [1000, 2000, 4000, 5000];

// Returning to a tab hidden for only a moment does not justify restarting a healthy
// stream. Longer absences usually do -- background connections get throttled, and
// nothing survives the machine sleeping.
const HIDDEN_RECONNECT_THRESHOLD = 10000;

export default function useMjpegStream(source) {
  const { generation } = storeToRefs(useStreamsStore());
  const online = useOnline();

  const imgRef = ref(null);
  // Changing the key replaces the <img> element outright. That -- not reassigning the
  // same src, which Vue skips as a no-op -- is what makes the browser reconnect.
  const streamKey = ref(0);

  let attempt = 0;
  let retryTimer = null;
  let hiddenAt = null;

  // Per-connection state, reset every time a new element is mounted.
  let connectedAt = 0;
  let everHadFrame = false;
  let zeroReads = 0;
  let pendingReconnect = false;

  const src = computed(() => toValue(source) ?? null);

  // While the browser reports no connectivity there is nothing to connect to. Dropping
  // the src releases the dead socket, so the reconnect on `online` starts from scratch.
  //
  // Reconnects also need a URL the browser has not seen before. Replacing the element
  // is not enough on its own: for an unchanged URL Chrome serves the image it already
  // has and never opens a socket, which would make a reload silently do nothing. The
  // first connection keeps the clean URL; every retry carries the key as a nonce.
  const streamSrc = computed(() => {
    if (!online.value || !src.value) return null;
    if (streamKey.value === 0) return src.value;

    return `${src.value}${src.value.includes('?') ? '&' : '?'}_r=${streamKey.value}`;
  });

  const hasFrame = () => !!imgRef.value?.naturalWidth;

  function reconnect({ immediate = false } = {}) {
    if (pendingReconnect && !immediate) return;

    pendingReconnect = true;
    clearTimeout(retryTimer);

    if (immediate) attempt = 0;

    const delay = immediate ? 0 : RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)];
    attempt += 1;

    retryTimer = setTimeout(() => {
      retryTimer = null;
      streamKey.value += 1;
    }, delay);
  }

  function checkLiveness() {
    // No element means the card is showing a placeholder instead of the stream (station
    // unhealthy), so there is no connection to watch and nothing to recover.
    if (!streamSrc.value || !imgRef.value || pendingReconnect) return;

    if (hasFrame()) {
      everHadFrame = true;
      zeroReads = 0;
      // A stream that is delivering earns a fast first retry next time it breaks.
      attempt = 0;
      return;
    }

    if (!everHadFrame) {
      if (Date.now() - connectedAt >= FIRST_FRAME_TIMEOUT) reconnect();
      return;
    }

    zeroReads += 1;
    if (zeroReads >= DEAD_READS) reconnect();
  }

  const livenessTimer = setInterval(checkLiveness, LIVENESS_POLL);

  // Runs once the element is in the DOM, after every reconnect, src change, and after the
  // card swaps back from its placeholder to a live stream.
  watch(
    [streamKey, streamSrc, imgRef],
    () => {
      pendingReconnect = false;
      connectedAt = Date.now();
      everHadFrame = false;
      zeroReads = 0;
    },
    { immediate: true, flush: 'post' },
  );

  // Not every browser stays silent about a cut stream; when one does report it, take
  // the shortcut rather than waiting for the poll to notice.
  useEventListener(imgRef, 'error', () => reconnect());

  useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }

    const hiddenFor = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = null;

    if (hiddenFor >= HIDDEN_RECONNECT_THRESHOLD) reconnect({ immediate: true });
  });

  watch(online, (isOnline) => {
    if (isOnline) reconnect({ immediate: true });
  });

  watch(generation, () => reconnect({ immediate: true }));

  onScopeDispose(() => {
    clearInterval(livenessTimer);
    clearTimeout(retryTimer);
    // A detached <img> can hold its socket open for a while. Removing the attribute
    // aborts it now; assigning '' would instead re-request the page URL.
    imgRef.value?.removeAttribute('src');
  });

  return {
    imgRef,
    streamKey,
    streamSrc,

    reload: () => reconnect({ immediate: true }),
  };
}
